import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { tr } from '@/contexts/LanguageContext';

// Paso 2 — 2FA. Escucha el evento global que emite lib/api.js cuando el backend responde
// 403 STEP_UP_REQUIRED (acción sensible) y pide el código; al validar, la petición original se reintenta.
// Con 403 MFA_SETUP_REQUIRED (admin sin 2FA) lleva a la pantalla de activación.
export const StepUpDialog = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onStepUp = () => { setCode(''); setOpen(true); };
    const onSetup = () => {
      toast.error(tr("Tu cuenta de administración requiere activar la verificación en dos pasos."));
      navigate('/settings/account?mfa=1');
    };
    window.addEventListener('ziyup:step-up-required', onStepUp);
    window.addEventListener('ziyup:mfa-setup-required', onSetup);
    return () => {
      window.removeEventListener('ziyup:step-up-required', onStepUp);
      window.removeEventListener('ziyup:mfa-setup-required', onSetup);
    };
  }, [navigate]);

  const finish = (ok) => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent('ziyup:step-up-done', { detail: { ok } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/mfa/step-up', { code: code.trim() });
      toast.success(tr("Verificado. Repetimos la acción."));
      finish(true);
    } catch (err) {
      toast.error(err?.message || tr("Código incorrecto."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
      <DialogContent className="max-w-sm" data-testid="step-up-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> {tr("Confirma con tu código 2FA")}</DialogTitle>
          <DialogDescription>{tr("Esta acción es sensible. Escribe el código de 6 dígitos de tu app autenticadora o un código de respaldo.")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={9} value={code}
                 onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="step-up-code-input" />
          <Button type="submit" className="w-full" disabled={busy || code.trim().length < 6} data-testid="step-up-submit">
            {tr("Verificar")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
